/**
 * points: [{ x, z, longitude, latitude }, ...] 최소 3개(권장 4개 이상)
 * 반환: { latLonToModelPosition, meta }
 */
export function createLatLngToModelTransformer(points) {
  if (!Array.isArray(points) || points.length < 3) {
    throw new Error('Need at least 3 points.');
  }

  // 수치 안정성을 위해 기준점을 평균 위경도로 잡음
  const referenceLatitude = points.reduce((sum, p) => sum + p.latitude, 0) / points.length;
  const referenceLongitude = points.reduce((sum, p) => sum + p.longitude, 0) / points.length;

  // 위경도 -> 로컬 평면(미터) 근사 변환 계수
  const degToRad = Math.PI / 180;
  const metersPerDegreeLatitude = 111319.49079327357;
  const metersPerDegreeLongitude = metersPerDegreeLatitude * Math.cos(referenceLatitude * degToRad);

  function latLonToEastNorthMeters(latitude, longitude) {
    const eastMeters = (longitude - referenceLongitude) * metersPerDegreeLongitude;
    const northMeters = (latitude - referenceLatitude) * metersPerDegreeLatitude;
    return { eastMeters, northMeters };
  }

  // A 행렬(디자인 행렬)과 타겟 벡터 구성
  // 모델: x = ax*E + bx*N + tx
  //       z = az*E + bz*N + tz
  const designMatrix = points.map((p) => {
    const { eastMeters, northMeters } = latLonToEastNorthMeters(p.latitude, p.longitude);
    return [eastMeters, northMeters, 1];
  });

  const targetX = points.map((p) => p.x);
  const targetZ = points.map((p) => p.z);

  const xTransformCoefficients = solveLeastSquares3Params(designMatrix, targetX); // [coefEastToX, coefNorthToX, translateX]
  const zTransformCoefficients = solveLeastSquares3Params(designMatrix, targetZ); // [coefEastToZ, coefNorthToZ, translateZ]

  function latLonToModelPosition(latitude, longitude) {
    const { eastMeters, northMeters } = latLonToEastNorthMeters(latitude, longitude);

    const [coefEastToX, coefNorthToX, translateX] = xTransformCoefficients;
    const [coefEastToZ, coefNorthToZ, translateZ] = zTransformCoefficients;

    const x = coefEastToX * eastMeters + coefNorthToX * northMeters + translateX;
    const z = coefEastToZ * eastMeters + coefNorthToZ * northMeters + translateZ;

    return { x, z };
  }

  function getFitRMSE() {
    let squaredErrorSum = 0;
    for (const p of points) {
      const predicted = latLonToModelPosition(p.latitude, p.longitude);
      const dx = predicted.x - p.x;
      const dz = predicted.z - p.z;
      squaredErrorSum += dx * dx + dz * dz;
    }
    return Math.sqrt(squaredErrorSum / points.length);
  }

  const [coefEastToX, coefNorthToX] = xTransformCoefficients;
  const [coefEastToZ, coefNorthToZ] = zTransformCoefficients;

  // 동(E) 방향이 모델에서 향하는 각도 (모델 +x 기준, z축을 위로 놓고 보는 2D 각도)
  const eastHeadingRad = Math.atan2(coefEastToZ, coefEastToX);
  const eastHeadingDeg = (eastHeadingRad * 180) / Math.PI;

  return {
    latLngToModelPosition: latLonToModelPosition,
    meta: {
      rotationDeg: eastHeadingDeg,
      referenceLatitude,
      referenceLongitude,
      metersPerDegreeLatitude,
      metersPerDegreeLongitude,
      xTransformCoefficients, // [E->X, N->X, X-translation]
      zTransformCoefficients, // [E->Z, N->Z, Z-translation]
      fitRMSE: getFitRMSE()
    }
  };
}

/**
 * 최소제곱 해 구하기 (3 파라미터: [east, north, 1])
 * params = argmin ||designMatrix * params - target||
 * params = (A^T A)^-1 A^T b
 */
function solveLeastSquares3Params(designMatrix, targetVector) {
  // normalMatrix = A^T A (3x3)
  const normalMatrix = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0]
  ];

  // normalVector = A^T b (3)
  const normalVector = [0, 0, 0];

  for (let rowIndex = 0; rowIndex < designMatrix.length; rowIndex++) {
    const [eastMeters, northMeters, constantTerm] = designMatrix[rowIndex];
    const targetValue = targetVector[rowIndex];

    // A^T A 누적
    normalMatrix[0][0] += eastMeters * eastMeters;
    normalMatrix[0][1] += eastMeters * northMeters;
    normalMatrix[0][2] += eastMeters * constantTerm;

    normalMatrix[1][0] += northMeters * eastMeters;
    normalMatrix[1][1] += northMeters * northMeters;
    normalMatrix[1][2] += northMeters * constantTerm;

    normalMatrix[2][0] += constantTerm * eastMeters;
    normalMatrix[2][1] += constantTerm * northMeters;
    normalMatrix[2][2] += constantTerm * constantTerm;

    // A^T b 누적
    normalVector[0] += eastMeters * targetValue;
    normalVector[1] += northMeters * targetValue;
    normalVector[2] += constantTerm * targetValue;
  }

  const inverseNormalMatrix = invert3x3Matrix(normalMatrix);
  return multiply3x3MatrixByVector(inverseNormalMatrix, normalVector);
}

function multiply3x3MatrixByVector(matrix3x3, vector3) {
  return [
    matrix3x3[0][0] * vector3[0] + matrix3x3[0][1] * vector3[1] + matrix3x3[0][2] * vector3[2],
    matrix3x3[1][0] * vector3[0] + matrix3x3[1][1] * vector3[1] + matrix3x3[1][2] * vector3[2],
    matrix3x3[2][0] * vector3[0] + matrix3x3[2][1] * vector3[1] + matrix3x3[2][2] * vector3[2]
  ];
}

function invert3x3Matrix(matrix) {
  const m00 = matrix[0][0],
    m01 = matrix[0][1],
    m02 = matrix[0][2];
  const m10 = matrix[1][0],
    m11 = matrix[1][1],
    m12 = matrix[1][2];
  const m20 = matrix[2][0],
    m21 = matrix[2][1],
    m22 = matrix[2][2];

  // 여인수(코팩터)들
  const cofactor00 = m11 * m22 - m12 * m21;
  const cofactor01 = -(m10 * m22 - m12 * m20);
  const cofactor02 = m10 * m21 - m11 * m20;

  const determinant = m00 * cofactor00 + m01 * cofactor01 + m02 * cofactor02;

  if (Math.abs(determinant) < 1e-12) {
    throw new Error('Matrix is singular. Points may be collinear or unstable.');
  }

  const inverseDeterminant = 1 / determinant;

  // adjugate(전치된 코팩터 행렬) * 1/det
  const inverseMatrix = [
    [
      cofactor00 * inverseDeterminant,
      -(m01 * m22 - m02 * m21) * inverseDeterminant,
      (m01 * m12 - m02 * m11) * inverseDeterminant
    ],
    [
      cofactor01 * inverseDeterminant,
      (m00 * m22 - m02 * m20) * inverseDeterminant,
      -(m00 * m12 - m02 * m10) * inverseDeterminant
    ],
    [
      cofactor02 * inverseDeterminant,
      -(m00 * m21 - m01 * m20) * inverseDeterminant,
      (m00 * m11 - m01 * m10) * inverseDeterminant
    ]
  ];

  return inverseMatrix;
}
